import { useTranslation } from 'next-i18next';

import { forwardRef, useCallback, useState } from 'react';

import { Box, Button, Flex, Heading, Separator, Text, useBreakpointValue } from '@chakra-ui/react';

import isNil from 'lodash/isNil';

import { ScrollableWrapper } from '@nocowanie/common-ui/components/molecules/scrollable-wrapper';

import { ReviewItem } from '@app/components/molecules/review-item';
import { ReviewSummary } from '@app/components/molecules/review-summary';
import { ReviewsHeader } from '@app/components/molecules/reviews-header';
import { OfferManager } from '@app/components/organisms/offer-manager';
import { ReviewsModal } from '@app/components/organisms/reviews-modal';
import { AppModalsEnum } from '@app/enums';
import { useDrawerState } from '@app/hooks/useDrawerState';
import * as accommodationRatingsSlice from '@app/state/slices/accommodation-ratings.slice';
import * as accommodationStaticSlice from '@app/state/slices/accommodation-static.slice';
import * as commonUiStateSlice from '@app/state/slices/common-ui-state.slice';
import { useAppDispatch, useAppSelector } from '@app/state/state';

import { ReviewsProps } from './reviews.props';

export const Reviews = forwardRef<HTMLDivElement, any>((props: ReviewsProps, ref) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'reviews',
    });
    const dispatch = useAppDispatch();

    const { ratingAverage, ratingCount } = useAppSelector(
        accommodationStaticSlice.selectors.selectReviewsData,
    );
    const recentReviews = useAppSelector(accommodationStaticSlice.selectors.selectRecentReviews);
    const [reviewIndex, setReviewIndex] = useState<number | null>(null);
    const accommodation = useAppSelector(accommodationStaticSlice.selectors.selectAccommodation);
    const isInitialDataLoaded = useAppSelector(
        accommodationRatingsSlice.selectors.selectIsInitialDataLoaded,
    );
    const accommodationId = accommodation?.id || undefined;
    const fetchData = useCallback(() => {
        if (!accommodationId) {
            return;
        }

        dispatch(
            accommodationRatingsSlice.actions.fetchRatingsThunk({
                accommodationId,
            }),
        );
    }, [accommodationId]);

    const { isDrawerPending, openDrawer } = useDrawerState(() => {
        dispatch(
            commonUiStateSlice.actions.setModalDataAction({
                modalName: AppModalsEnum.Reviews,
                modalData: true,
            }),
        );

        if (!isInitialDataLoaded) {
            setTimeout(() => {
                fetchData();
            }, 0);
        }
    });

    const onReviewClick = useCallback(
        (id: number) => {
            setReviewIndex(id);
            openDrawer();
        },
        [openDrawer],
    );

    const scrollableStyles = {
        maxW: { base: '100vw', md: '100%' },
        gridAutoColumns: { base: '278px', md: '270px' },
        gap: {
            base: 'gutterWidth',
            lg: 'gutterWidthDesktop',
        },
        ml: 0,
        px: { base: 4, md: 0 },
        pt: 2,
    };

    const isMobileRes = useBreakpointValue({
        base: true,
        md: false,
    });

    const onTriggerClick = useCallback(() => {
        setTimeout(() => {
            openDrawer();
        }, 0);
    }, [openDrawer]);

    return ratingCount ? (
        <Box ref={ref} data-testid={'reviews-section'}>
            <Heading size={'md'} mb={{ base: 4, md: 6 }} data-testid={'reviews-section-header'}>
                {t('reviewsHeader')}
            </Heading>
            <Flex
                direction={isMobileRes ? 'column' : 'row'}
                mb={4}
                flexWrap={'wrap'}
                gap={'gutterWidth'}
                justifyContent={'space-between'}
            >
                <ReviewsHeader
                    as={'button'}
                    type={'button'}
                    aria-label={t('seeAll', {
                        value: ratingCount,
                    })}
                    onClick={openDrawer}
                    cursor={'pointer'}
                    maxW={'max-content'}
                />
                <ReviewSummary minW={0} />
            </Flex>
            {!isNil(recentReviews) ? (
                <Box mb={5}>
                    <>
                        <Text fontWeight={'bold'} mb={{ base: 2, md: 1 }} pt={2} hideBelow={'md'}>
                            {t('lastReviewHeader')}
                        </Text>
                        <Box mx={{ base: -4, md: 0 }}>
                            <ScrollableWrapper
                                scrollableContainerProps={scrollableStyles}
                                showArrows={!isMobileRes}
                                arrowOffset={-5}
                                scrollOffset={294}
                            >
                                {(recentReviews ?? []).map((_, index) => (
                                    <ReviewItem
                                        key={index}
                                        review={recentReviews?.[index]}
                                        onExpandClick={() =>
                                            setTimeout(() => onReviewClick(index), 0)
                                        }
                                        isPreview
                                    />
                                ))}
                            </ScrollableWrapper>
                        </Box>
                    </>
                </Box>
            ) : null}

            {!ratingAverage || !ratingCount ? null : (
                <>
                    <Box mb={5} mt={6} textAlign={'center'}>
                        <Button
                            onClick={onTriggerClick}
                            variant={'outline'}
                            width={{
                                base: '100%',
                                md: 'auto',
                            }}
                            loading={isDrawerPending}
                            disabled={isDrawerPending}
                            data-testid={'reviews-section-see-all'}
                        >
                            {t('seeAll', {
                                value: ratingCount,
                            })}
                        </Button>
                    </Box>

                    <ReviewsModal
                        onDrawerOpen={openDrawer}
                        reviewItemIndex={reviewIndex}
                        setReviewItemIndex={setReviewIndex}
                        fetchData={fetchData}
                    />
                </>
            )}

            <Separator mt={6} />
        </Box>
    ) : (
        <OfferManager />
    );
});

Reviews.displayName = 'Reviews';
